<template>
  <section class="workspace-stack">
    <article class="fomo-dashboard-card finance-workspace">
      <div class="workspace-panel__header finance-hero">
        <div>
          <span class="workspace-kicker">Finanzas del evento</span>
          <h2 class="workspace-title">{{ showInternalFinance ? 'Resumen financiero' : 'Balance' }}</h2>
          <p class="workspace-copy">{{ showInternalFinance ? 'Ventas, costos internos y transferencias en una sola lectura.' : 'Lo generado, lo que ya recibiste y lo que sigue pendiente, sin vueltas.' }}</p>
        </div>

        <div class="finance-header-actions">
          <button v-if="canViewInternalFinance" class="workspace-button workspace-button--small workspace-button--ghost" type="button" @click="hostViewPreview = !hostViewPreview">
            {{ hostViewPreview ? 'Ver como admin' : 'Ver como host' }}
          </button>

          <div v-if="showInternalFinance" :class="['finance-health', { 'finance-health--ok': event.ledger_is_reconciled, 'finance-health--warn': !event.ledger_is_reconciled }]">
            <small>{{ event.ledger_is_reconciled ? 'Ledger reconciliado' : 'Ledger por revisar' }}</small>
            <strong>{{ event.ledger_is_reconciled ? 'OK' : `${event.ledger_missing_posted_payments} faltantes` }}</strong>
            <span>{{ ledgerHealthCopy }}</span>
          </div>
        </div>
      </div>

      <div class="finance-overview">
        <section class="fomo-dashboard-card finance-balance-card" aria-labelledby="organizer-balance-title">
          <header class="finance-card-heading">
            <div>
              <span class="finance-eyebrow">Balance del organizador</span>
              <h3 id="organizer-balance-title">Total generado</h3>
            </div>
            <span class="finance-currency-chip">{{ eventCurrency }}</span>
          </header>

          <strong class="finance-balance-card__amount">{{ formatMoney(organizerGrossAmount) }}</strong>
          <p>Ganancia neta registrada para el organizador en este evento.</p>

          <div class="finance-progress" role="progressbar" aria-label="Porcentaje acreditado" aria-valuemin="0" aria-valuemax="100" :aria-valuenow="Math.round(organizerPaidProgress)">
            <span :style="{ width: `${organizerPaidProgress}%` }"></span>
          </div>

          <div class="finance-balance-stats">
            <article>
              <small>Transferido por Fomo</small>
              <strong>{{ formatMoney(organizerDepositedAmount) }}</strong>
              <span>Payouts y depósitos registrados</span>
            </article>
            <article>
              <small>Acreditación directa</small>
              <strong>{{ formatMoney(organizerDirectAmount) }}</strong>
              <span>Recibido en tus cajas, sin payout</span>
            </article>
            <article>
              <small>Aún pendiente</small>
              <strong>{{ formatMoney(organizerPendingAmount) }}</strong>
              <span>Incluye saldos todavía retenidos</span>
            </article>
          </div>
        </section>

        <section class="fomo-dashboard-card finance-availability" aria-labelledby="available-balance-title">
          <header class="finance-card-heading">
            <div>
              <span class="finance-eyebrow">Saldo pendiente</span>
              <h3 id="available-balance-title">Estado de acreditación</h3>
            </div>
            <i class="fas fa-chart-pie" aria-hidden="true"></i>
          </header>

          <div v-if="settlementSummaries.length" class="finance-availability__list">
            <article v-for="settlement in settlementSummaries" :key="settlement.currency" class="finance-availability__currency">
              <div class="finance-pending-intro">
                <span>{{ settlement.currency }}</span>
                <p>Así se distribuye lo que todavía no ha sido transferido.</p>
              </div>
              <div class="finance-pending-track" :aria-label="`Detalle del saldo pendiente en ${settlement.currency}`">
                <span v-if="settlement.availableShare > 0" class="is-ready" :style="{ width: `${settlement.availableShare}%` }"></span>
                <span v-if="settlement.reservedShare > 0" class="is-moving" :style="{ width: `${settlement.reservedShare}%` }"></span>
                <span v-if="settlement.heldShare > 0" class="is-held" :style="{ width: `${settlement.heldShare}%` }"></span>
              </div>
              <dl class="finance-pending-details">
                <div>
                  <dt><i class="is-ready"></i>Listo para nueva transferencia</dt>
                  <dd>{{ formatMoney(settlement.available, settlement.currency) }}</dd>
                </div>
                <div v-if="settlement.reserved > 0">
                  <dt><i class="is-moving"></i>En proceso</dt>
                  <dd>{{ formatMoney(settlement.reserved, settlement.currency) }}</dd>
                </div>
                <div v-if="settlement.held > 0">
                  <dt><i class="is-held"></i>Aún no liberado</dt>
                  <dd>{{ formatMoney(settlement.held, settlement.currency) }}</dd>
                </div>
              </dl>
            </article>
          </div>
          <div v-else-if="payoutLoading" class="finance-inline-state">
            <i class="fas fa-circle-notch fa-spin" aria-hidden="true"></i>
            Cargando disponibilidad…
          </div>
          <div v-else-if="payoutError" class="finance-inline-state is-error">{{ payoutError }}</div>
          <div v-else class="finance-inline-state">Aún no hay saldo liberable para este evento.</div>
        </section>
      </div>

      <section v-if="fundingMethodBreakdown.length" class="fomo-dashboard-card finance-methods" aria-labelledby="funding-methods-title">
        <header class="finance-section-heading">
          <div>
            <span class="finance-eyebrow">Composición del balance</span>
            <h3 id="funding-methods-title">Fondos por método de cobro</h3>
          </div>
          <p>Si recibe Fomo, se liquida por payout. Si recibes tú, se acredita directamente.</p>
        </header>

        <div class="finance-methods__list">
          <article v-for="method in fundingMethodBreakdown" :key="method.key" :class="['fomo-dashboard-card', 'finance-method', fundingSourceClass(method.source)]">
            <div class="finance-method__icon">
              <i :class="fundingSourceIcon(method.source)" aria-hidden="true"></i>
            </div>
            <div class="finance-method__body">
              <header>
                <div>
                  <strong>{{ method.label }}</strong>
                  <span v-if="method.paymentsCount > 0">{{ method.paymentsCount }} {{ method.paymentsCount === 1 ? 'cobro confirmado' : 'cobros confirmados' }}</span>
                  <span v-else>Conteo de pagos no disponible</span>
                </div>
                <strong>{{ formatMoney(method.amount, method.currency) }}</strong>
              </header>
              <div class="finance-method__bar-label">
                <span>{{ method.shareLabel }} del balance {{ method.currency }}</span>
                <strong>{{ method.direct ? 'Acreditación directa' : `${method.paidShareLabel} transferido` }}</strong>
              </div>
              <div class="finance-method__allocation" :aria-label="`Distribución de ${method.label}: ${method.paidShareLabel} ${method.direct ? 'acreditado directamente' : 'transferido'}`">
                <span v-if="method.paidShare > 0" class="is-paid" :style="{ width: `${method.paidShare}%` }"></span>
                <span v-if="method.reservedShare > 0" class="is-reserved" :style="{ width: `${method.reservedShare}%` }"></span>
                <span v-if="method.availableShare > 0" class="is-available" :style="{ width: `${method.availableShare}%` }"></span>
              </div>
              <footer>
                <span v-if="method.paid > 0.005" class="finance-method__legend is-paid"><i></i>{{ method.direct ? 'Recibido directamente' : 'Transferido' }} {{ formatMoney(method.paid, method.currency) }}</span>
                <span v-if="method.reserved > 0.005" class="finance-method__legend is-reserved"><i></i>En proceso {{ formatMoney(method.reserved, method.currency) }}</span>
                <span v-if="method.available > 0.005" class="finance-method__legend is-available"><i></i>Disponible {{ formatMoney(method.available, method.currency) }}</span>
                <span v-if="method.pending > 0.005" class="finance-method__legend is-pending"><i></i>Pendiente {{ formatMoney(method.pending, method.currency) }}</span>
              </footer>
              <p v-if="method.charged !== undefined" class="finance-method__collection">Recaudado {{ formatMoney(method.charged, method.currency) }} · Neto del organizador {{ formatMoney(method.amount, method.currency) }}<template v-if="method.refunded > 0"> · Devuelto {{ formatMoney(method.refunded, method.currency) }}</template></p>
            </div>
          </article>
        </div>
      </section>

      <div v-if="showInternalFinance" class="finance-admin-strip">
        <article>
          <small>Ventas ticket</small>
          <strong>{{ formatMoney(event.total_ticket_sales) }}</strong>
        </article>
        <article>
          <small>Fee FOMO</small>
          <strong>{{ formatMoney(event.ledger_platform_fee_gross) }}</strong>
        </article>
        <article>
          <small>FOMO Safe</small>
          <strong>{{ formatMoney(event.ledger_fomo_safe_gross) }}</strong>
        </article>
        <article>
          <small>Costo pasarela</small>
          <strong>{{ formatMoney(event.ledger_processor_fee_total) }}</strong>
        </article>
      </div>

      <div v-if="orders.length" class="workspace-table-shell">
        <table class="workspace-table payout-table">
          <thead>
            <tr>
              <th>Movimiento</th>
              <th>Método</th>
              <th>Origen de fondos</th>
              <th>Monto</th>
              <th>Estado</th>
              <th>Referencia</th>
              <th><span class="sr-only">Acciones</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="order in orders" :key="order.id" :class="{ 'is-open': selectedOrder && selectedOrder.id === order.id }">
              <td>
                <div class="payout-table__primary">
                  <strong>#{{ order.id }}</strong>
                  <small>{{ formatDate(order.transfer_date || order.created_at) }}</small>
                </div>
              </td>
              <td>
                <div class="payout-table__primary">
                  <strong>{{ railText(order.rail, order.rail_label) }}</strong>
                  <small>{{ executionModeText(order.execution_mode, order.execution_mode_label) }}</small>
                </div>
              </td>
              <td>
                <div class="payout-table__primary">
                  <strong>{{ fundingSourceText(order.funding_source, order.funding_source_label) }}</strong>
                  <small>{{ includedSourcesLabel(order) }}</small>
                </div>
              </td>
              <td class="payout-table__amount">{{ formatMoney(order.amount, order.currency || eventCurrency) }}</td>
              <td><span :class="['payout-status', statusClass(order.status)]">{{ payoutStatusText(order.status, order.status_label) }}</span></td>
              <td>{{ payoutReference(order) }}</td>
              <td class="payout-table__action">
                <button class="payout-detail-button" type="button" @click="openPayoutDetails(order)">
                  <span>Ver detalle</span>
                  <i class="fas fa-arrow-right" aria-hidden="true"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else-if="!payoutLoading && !payoutError" class="workspace-empty">Todavía no hay transferencias registradas para este evento.</div>

      <Teleport to="body">
        <Transition name="payout-modal">
          <div v-if="selectedOrder" class="payout-modal-backdrop" @click.self="closePayoutDetails">
            <section class="payout-modal" role="dialog" aria-modal="true" aria-labelledby="payout-detail-title">
              <header class="payout-modal__header">
                <div>
                  <span class="payout-modal__eyebrow">Movimiento bancario</span>
                  <h3 id="payout-detail-title">Transferencia #{{ selectedOrder.id }}</h3>
                  <p>{{ event.name }} · {{ formatDateTime(selectedOrder.transfer_date || selectedOrder.created_at) }}</p>
                </div>
                <div class="payout-modal__header-actions">
                  <span :class="['payout-status', statusClass(selectedOrder.status)]">{{ payoutStatusText(selectedOrder.status, selectedOrder.status_label) }}</span>
                  <button class="payout-modal__close" type="button" aria-label="Cerrar detalle" @click="closePayoutDetails">
                    <i class="fas fa-times" aria-hidden="true"></i>
                  </button>
                </div>
              </header>

              <div class="payout-modal__body">
                <section class="payout-detail-summary" aria-label="Resumen del movimiento">
                  <article>
                    <small>Total de esta transferencia</small>
                    <strong>{{ formatMoney(selectedOrder.amount, selectedOrder.currency || eventCurrency) }}</strong>
                  </article>
                  <article>
                    <small>Método</small>
                    <strong>{{ railText(selectedOrder.rail, selectedOrder.rail_label) }}</strong>
                  </article>
                  <article>
                    <small>Referencia bancaria</small>
                    <strong>{{ payoutReference(selectedOrder) }}</strong>
                  </article>
                  <article>
                    <small>Orígenes de fondos</small>
                    <strong>{{ includedSourcesLabel(selectedOrder) }}</strong>
                  </article>
                </section>

                <div class="payout-modal__columns">
                  <section class="payout-detail-card payout-proof-card">
                    <div class="payout-detail-card__heading">
                      <div>
                        <span class="payout-modal__eyebrow">Respaldo</span>
                        <h4>Comprobante de transferencia</h4>
                      </div>
                      <a v-if="proofUrl(selectedOrder)" class="payout-text-link" :href="proofUrl(selectedOrder)" target="_blank" rel="noopener noreferrer">
                        Abrir original <i class="fas fa-up-right-from-square" aria-hidden="true"></i>
                      </a>
                    </div>

                    <a v-if="proofUrl(selectedOrder) && isImageProof(selectedOrder) && !proofPreviewFailed" class="payout-proof-preview" :href="proofUrl(selectedOrder)" target="_blank" rel="noopener noreferrer">
                      <img :src="proofUrl(selectedOrder)" :alt="`Comprobante de la transferencia #${selectedOrder.id}`" @error="proofPreviewFailed = true">
                      <span><i class="fas fa-magnifying-glass-plus" aria-hidden="true"></i> Ampliar comprobante</span>
                    </a>

                    <div v-else-if="proofUrl(selectedOrder)" class="payout-proof-file">
                      <i class="fas fa-file-shield" aria-hidden="true"></i>
                      <strong>Comprobante adjunto</strong>
                      <span>El archivo está disponible para revisión en su formato original.</span>
                      <a :href="proofUrl(selectedOrder)" target="_blank" rel="noopener noreferrer">Abrir archivo</a>
                    </div>

                    <div v-else class="payout-proof-empty">
                      <i class="fas fa-receipt" aria-hidden="true"></i>
                      <strong>{{ selectedOrder.status === 'paid' ? 'Sin comprobante adjunto' : 'Comprobante pendiente' }}</strong>
                      <span>{{ selectedOrder.status === 'paid' ? 'La transferencia fue registrada sin una imagen disponible.' : 'El comprobante aparecerá cuando FOMO complete la transferencia.' }}</span>
                    </div>
                  </section>

                  <section class="payout-detail-card payout-movement-card">
                    <div class="payout-detail-card__heading">
                      <div>
                        <span class="payout-modal__eyebrow">Información</span>
                        <h4>Datos del movimiento</h4>
                      </div>
                    </div>
                    <dl class="payout-definition-list">
                      <div><dt>Fecha de creación</dt><dd>{{ formatDateTime(selectedOrder.created_at) }}</dd></div>
                      <div><dt>Fecha de envío</dt><dd>{{ formatDateTime(selectedOrder.sent_at) }}</dd></div>
                      <div><dt>Fecha de pago</dt><dd>{{ formatDateTime(selectedOrder.paid_at) }}</dd></div>
                      <div><dt>Origen</dt><dd>{{ fundingSourceText(selectedOrder.funding_source, selectedOrder.funding_source_label) }}</dd></div>
                    </dl>
                    <div v-if="selectedOrder.comment" class="payout-note">
                      <small>Nota</small>
                      <p>{{ selectedOrder.comment }}</p>
                    </div>
                    <div v-if="selectedOrder.failure_reason" class="payout-note is-danger">
                      <small>Motivo de fallo</small>
                      <p>{{ selectedOrder.failure_reason }}</p>
                    </div>
                  </section>
                </div>

                <section class="payout-detail-card payout-included-card">
                  <div class="payout-detail-card__heading">
                    <div>
                      <span class="payout-modal__eyebrow">Desglose</span>
                      <h4>Orígenes usados</h4>
                      <p>Cada monto indica cuánto se tomó de ese origen para completar esta transferencia.</p>
                    </div>
                    <span class="payout-count-badge">{{ includedSourcesLabel(selectedOrder) }}</span>
                  </div>

                  <div class="payout-allocation-note">
                    <i class="fas fa-layer-group" aria-hidden="true"></i>
                    <div>
                      <strong>Cada origen se construye con payments conciliados</strong>
                      <span>Puedes revisar los movimientos que componen el bucket completo. En transferencias parciales, ese listado explica el origen del saldo, pero no asigna payments exclusivos a esta transferencia.</span>
                    </div>
                  </div>

                  <div v-if="partialSourcesCount(selectedOrder)" class="payout-partial-summary">
                    <i class="fas fa-circle-half-stroke" aria-hidden="true"></i>
                    <div>
                      <strong>{{ partialSourcesCount(selectedOrder) }} {{ partialSourcesCount(selectedOrder) === 1 ? 'origen parcialmente pagado' : 'orígenes parcialmente pagados' }}</strong>
                      <span>El monto tomado es menor que el saldo pagable del origen. El remanente queda disponible o reservado para otros movimientos.</span>
                    </div>
                  </div>

                  <div v-if="selectedOrder.items && selectedOrder.items.length" class="payout-included-list">
                    <article v-for="(item, index) in selectedOrder.items" :key="item.id" class="payout-included-item">
                      <div class="payout-included-item__index">{{ index + 1 }}</div>
                      <div class="payout-included-item__copy">
                        <div class="payout-included-item__title">
                          <div>
                            <small>Origen {{ index + 1 }}</small>
                            <strong>{{ itemSourceText(item, selectedOrder) }}</strong>
                            <span v-if="itemIsPartial(item)" class="payout-partial-pill">Pago parcial</span>
                          </div>
                          <div class="payout-included-item__amount">
                            <small>Tomado para esta transferencia</small>
                            <strong>{{ formatMoney(item.amount, itemSourceCurrency(item, selectedOrder)) }}</strong>
                          </div>
                        </div>
                        <div class="payout-included-item__meta">
                          <span><i class="fas fa-calendar-check" aria-hidden="true"></i>{{ sourceWindowText(item.source_window, item.source_window_label) }}</span>
                          <span><i class="fas fa-circle-check" aria-hidden="true"></i>{{ settlementStatusText(item.settlement_status, item.settlement_status_label) }}</span>
                        </div>
                        <div v-if="itemIsPartial(item)" class="payout-partial-notice">
                          <i class="fas fa-circle-info" aria-hidden="true"></i>
                          <span>
                            Se toman <strong>{{ formatMoney(item.amount, itemSourceCurrency(item, selectedOrder)) }}</strong> de este saldo agrupado.
                            Los payments mostrados debajo componen el bucket completo, no únicamente esta transferencia parcial.
                            <template v-if="itemUnallocatedBalance(item) > 0">
                              Quedan <strong>{{ formatMoney(itemUnallocatedBalance(item), itemSourceCurrency(item, selectedOrder)) }}</strong> sin pagar ni reservar.
                            </template>
                            <template v-else>El resto ya fue pagado o reservado en otros movimientos.</template>
                          </span>
                        </div>
                        <details v-if="item.funding_bucket_detail" class="payout-bucket-details">
                          <summary>Ver contexto contable del origen</summary>
                          <div>
                            <span><small>Cobrado en el origen</small><strong>{{ formatMoney(item.funding_bucket_detail.collected_amount, item.funding_bucket_detail.currency) }}</strong></span>
                            <span><small>Total pagable</small><strong>{{ formatMoney(itemSourceTotal(item), item.funding_bucket_detail.currency) }}</strong></span>
                            <span class="is-current"><small>Tomado en este movimiento</small><strong>{{ formatMoney(item.amount, item.funding_bucket_detail.currency) }}</strong></span>
                            <span><small>Pagado acumulado</small><strong>{{ formatMoney(item.funding_bucket_detail.paid_amount, item.funding_bucket_detail.currency) }}</strong></span>
                            <span><small>Reservado</small><strong>{{ formatMoney(item.funding_bucket_detail.reserved_amount, item.funding_bucket_detail.currency) }}</strong></span>
                            <span><small>Saldo sin asignar</small><strong>{{ formatMoney(itemUnallocatedBalance(item), item.funding_bucket_detail.currency) }}</strong></span>
                          </div>
                        </details>

                        <div v-if="item.funding_bucket_detail" class="payout-bucket-payments">
                          <button class="payout-bucket-payments__toggle" type="button" :aria-expanded="bucketPaymentPanel(item).open ? 'true' : 'false'" @click="toggleBucketPayments(item)">
                            <span>
                              <i class="fas fa-receipt" aria-hidden="true"></i>
                              {{ bucketPaymentPanel(item).open ? 'Ocultar movimientos' : bucketPaymentsButtonLabel(item) }}
                            </span>
                            <i :class="bucketPaymentPanel(item).open ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" aria-hidden="true"></i>
                          </button>

                          <section v-if="bucketPaymentPanel(item).open" class="payout-bucket-payments__panel" aria-label="Payments que componen el origen">
                            <div class="payout-bucket-payments__context">
                              <div>
                                <small>Composición del bucket completo</small>
                                <strong>{{ formatMoney(itemSourceTotal(item), itemSourceCurrency(item, selectedOrder)) }}</strong>
                              </div>
                              <p>
                                Estos payments explican el saldo de este origen. La transferencia actual utilizó
                                <strong>{{ formatMoney(item.amount, itemSourceCurrency(item, selectedOrder)) }}</strong>;
                                no se atribuyen movimientos individuales a la porción transferida.
                              </p>
                            </div>

                            <div v-if="bucketPaymentPanel(item).loading" class="payout-bucket-payments__feedback">
                              <i class="fas fa-circle-notch fa-spin" aria-hidden="true"></i>
                              Cargando movimientos...
                            </div>
                            <div v-else-if="bucketPaymentPanel(item).error" class="payout-bucket-payments__feedback is-error">
                              <i class="fas fa-triangle-exclamation" aria-hidden="true"></i>
                              {{ bucketPaymentPanel(item).error }}
                              <button type="button" @click="loadBucketPayments(item, bucketPaymentPanel(item).page || 1)">Reintentar</button>
                            </div>
                            <template v-else-if="bucketPaymentPanel(item).data">
                              <div class="payout-bucket-payments__summary">
                                <span><strong>{{ bucketPaymentPanel(item).data.pagination.count }}</strong> {{ bucketPaymentPanel(item).data.pagination.count === 1 ? 'payment con aporte' : 'payments con aporte' }}</span>
                                <span v-if="bucketPaymentPanel(item).data.bucket.zero_value_payments_count"><strong>{{ bucketPaymentPanel(item).data.bucket.zero_value_payments_count }}</strong> aprobados en $0.00, separados</span>
                              </div>

                              <div v-if="bucketPaymentPanel(item).data.results.length" class="payout-bucket-payments__table-shell">
                                <table class="payout-bucket-payments__table">
                                  <thead>
                                    <tr>
                                      <th>Payment</th>
                                      <th>Fecha</th>
                                      <th>Método</th>
                                      <th>Total cobrado</th>
                                      <th>Aporte al organizador</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="payment in bucketPaymentPanel(item).data.results" :key="payment.id">
                                      <td><strong>#{{ payment.id }}</strong><small>Orden #{{ payment.invoice_id }}</small></td>
                                      <td>{{ formatDateTime(payment.date) }}</td>
                                      <td>{{ payment.payment_type_label }}</td>
                                      <td>{{ formatMoney(payment.charged_amount, payment.currency) }}</td>
                                      <td class="is-contribution">{{ formatMoney(payment.organizer_payable_amount, payment.currency) }}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                              <div v-else class="payout-bucket-payments__feedback">No hay payments monetarios posteados en este bucket.</div>

                              <footer v-if="bucketPaymentPanel(item).data.pagination.pages > 1" class="payout-bucket-payments__pagination">
                                <button type="button" :disabled="!bucketPaymentPanel(item).data.pagination.has_previous" @click="loadBucketPayments(item, bucketPaymentPanel(item).data.pagination.page - 1)">
                                  <i class="fas fa-arrow-left" aria-hidden="true"></i> Anterior
                                </button>
                                <span>Página {{ bucketPaymentPanel(item).data.pagination.page }} de {{ bucketPaymentPanel(item).data.pagination.pages }}</span>
                                <button type="button" :disabled="!bucketPaymentPanel(item).data.pagination.has_next" @click="loadBucketPayments(item, bucketPaymentPanel(item).data.pagination.page + 1)">
                                  Siguiente <i class="fas fa-arrow-right" aria-hidden="true"></i>
                                </button>
                              </footer>
                            </template>
                          </section>
                        </div>
                      </div>
                    </article>
                  </div>
                  <div v-else class="payout-proof-empty payout-proof-empty--compact">
                    <i class="fas fa-layer-group" aria-hidden="true"></i>
                    <strong>Sin orígenes individualizados</strong>
                    <span>Esta transferencia histórica no tiene buckets asociados.</span>
                  </div>

                  <footer :class="['payout-reconciliation', { 'is-ok': payoutIsReconciled(selectedOrder), 'is-warning': !payoutIsReconciled(selectedOrder) }]">
                    <div>
                      <small>Suma tomada de los orígenes</small>
                      <strong>{{ formatMoney(payoutItemsTotal(selectedOrder), selectedOrder.currency || eventCurrency) }}</strong>
                    </div>
                    <div>
                      <small>Total transferido</small>
                      <strong>{{ formatMoney(selectedOrder.amount, selectedOrder.currency || eventCurrency) }}</strong>
                    </div>
                    <span><i :class="payoutIsReconciled(selectedOrder) ? 'fas fa-circle-check' : 'fas fa-triangle-exclamation'" aria-hidden="true"></i>{{ payoutIsReconciled(selectedOrder) ? 'Montos conciliados' : 'Revisar diferencia de montos' }}</span>
                  </footer>
                </section>
              </div>
            </section>
          </div>
        </Transition>
      </Teleport>

      <section v-if="showInternalFinance" class="ledger-reconciliation">
        <header class="ledger-reconciliation__header">
          <div>
            <span class="ledger-reconciliation__eyebrow">Control contable interno</span>
            <h3>Conciliación de pagos</h3>
            <p>Primero se separan las cortesías y pagos sin monto; después se compara únicamente lo que sí debía entrar al ledger.</p>
          </div>
          <span :class="['ledger-reconciliation__status', { 'is-ok': ledgerMissingCount === 0, 'is-warning': ledgerMissingCount > 0 }]">
            <i :class="ledgerMissingCount === 0 ? 'fas fa-circle-check' : 'fas fa-triangle-exclamation'" aria-hidden="true"></i>
            {{ ledgerMissingCount === 0 ? 'Sin faltantes reales' : `${ledgerMissingCount} por revisar` }}
          </span>
        </header>

        <div class="ledger-equation" aria-label="Cálculo de pagos aprobados monetarios">
          <article class="ledger-equation__card">
            <small>Pagos aprobados</small>
            <strong>{{ ledgerApprovedCount }}</strong>
            <span>Estado aprobado en el evento</span>
          </article>
          <span class="ledger-equation__operator" aria-hidden="true">−</span>
          <article class="ledger-equation__card">
            <small>Sin monto contable</small>
            <strong>{{ ledgerZeroValueCount }}</strong>
            <span>Free, cortesías o total 0.00</span>
          </article>
          <span class="ledger-equation__operator" aria-hidden="true">=</span>
          <article class="ledger-equation__card ledger-equation__card--result">
            <small>Aprobados monetarios</small>
            <strong>{{ ledgerMonetaryApprovedCount }}</strong>
            <span>Asientos esperados en el ledger</span>
          </article>
        </div>

        <div class="ledger-flow" aria-label="Resultado de la conciliación con el ledger">
          <article>
            <small>Esperados en ledger</small>
            <strong>{{ ledgerMonetaryApprovedCount }}</strong>
          </article>
          <i class="fas fa-arrow-right ledger-flow__arrow" aria-hidden="true"></i>
          <article>
            <small>Asientos posteados</small>
            <strong>{{ ledgerPostedCount }}</strong>
          </article>
          <i class="fas fa-arrow-right ledger-flow__arrow" aria-hidden="true"></i>
          <article :class="['ledger-flow__result', { 'is-ok': ledgerMissingCount === 0, 'is-warning': ledgerMissingCount > 0 }]">
            <small>Faltantes reales</small>
            <strong>{{ ledgerMissingCount }}</strong>
          </article>
        </div>

        <footer class="ledger-reconciliation__source">
          <i class="fas fa-circle-info" aria-hidden="true"></i>
          Fuente: pagos aprobados y asientos contables posteados de este evento. Son conteos de pagos, no montos de dinero.
        </footer>
      </section>

    </article>
  </section>
</template>

<script>
import { apiService } from '../../common/api.services.js'
import { formatCurrencyAmount, normalizeCurrencyCode } from '../../common/currency.js'
import '../../styles/fomo-dashboard.css'

export default {
  name: 'EventFinance',

  props: {
    event: {
      required: true,
    },
  },

  data() {
    return {
      settlements: [],
      orders: [],
      cashierMethods: [],
      payoutRequestId: 0,
      payoutLoading: false,
      payoutError: '',
      hostViewPreview: false,
      selectedOrder: null,
      proofPreviewFailed: false,
      bodyOverflowBeforeModal: '',
      bucketPaymentPanels: {},
    }
  },

  created() {
    this.getPayoutSummary()
  },

  mounted() {
    window.addEventListener('keydown', this.handleModalKeydown)
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.handleModalKeydown)
    this.restoreBodyScroll()
  },

  watch: {
    event: {
      deep: true,
      handler() {
        this.closePayoutDetails()
        this.settlements = []
        this.orders = []
        this.cashierMethods = []
        this.payoutError = ''
        this.hostViewPreview = false
        this.bucketPaymentPanels = {}
        this.getPayoutSummary()
      },
    },
  },

  methods: {
    formatCount(value) {
      const parsed = Number.parseInt(value, 10)
      return Number.isNaN(parsed) ? 0 : parsed
    },

    parseDisplayDate(value) {
      const isDateOnly = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
      return new Date(isDateOnly ? `${value}T00:00:00` : value)
    },

    formatDate(value) {
      if (!value) {
        return 'Sin fecha'
      }
      const date = this.parseDisplayDate(value)
      if (Number.isNaN(date.getTime())) return 'Sin fecha'
      return new Intl.DateTimeFormat('default', { dateStyle: 'medium' }).format(date)
    },

    formatMoney(value, currency = this.eventCurrency) {
      return formatCurrencyAmount(value, currency || this.eventCurrency)
    },

    formatDateTime(value) {
      if (!value) return 'Pendiente'
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return this.formatDate(value)
      const date = this.parseDisplayDate(value)
      if (Number.isNaN(date.getTime())) return 'Pendiente'
      return new Intl.DateTimeFormat('default', { dateStyle: 'medium', timeStyle: 'short' }).format(date)
    },

    railText(value, fallback = '') {
      const labels = {
        stripe_global_payouts_api: 'Transferencia internacional',
        stripe_connect: 'Stripe Express',
        manual_bank_transfer: 'Transferencia bancaria',
        manual_external_provider: 'Proveedor de pagos',
      }
      return labels[value] || fallback || 'Transferencia'
    },

    fundingSourceText(value, fallback = '') {
      const labels = {
        stripe: 'Cobros con tarjeta',
        stripe_connect: 'Cobros con Stripe Express',
        paypal: 'Cobros con PayPal',
        rebill: 'Cobros con Rebill',
        bank_transfer: 'Transferencias bancarias',
        cash: 'Cobros en efectivo',
        fomo_cash: 'Efectivo (recibe Fomoplanes)',
        fomo_pos: 'POS (Gestionada por Fomoplanes)',
        organizer_cash: 'Efectivo (recibe el organizador)',
        organizer_pos: 'POS (Gestionada por organizador)',
        other: 'Fondos del evento',
      }
      return labels[value] || fallback || 'Fondos del evento'
    },

    fundingSourceIcon(value) {
      const icons = {
        stripe: 'fas fa-credit-card',
        stripe_connect: 'fas fa-credit-card',
        paypal: 'fab fa-paypal',
        rebill: 'fas fa-wallet',
        bank_transfer: 'fas fa-university',
        cash: 'fas fa-money-bill-wave',
        fomo_cash: 'fas fa-money-bill-wave',
        fomo_pos: 'fas fa-credit-card',
        organizer_cash: 'fas fa-money-bill-wave',
        organizer_pos: 'fas fa-credit-card',
        other: 'fas fa-layer-group',
      }
      return icons[value] || icons.other
    },

    fundingSourceClass(value) {
      const classes = {
        stripe: 'is-card',
        stripe_connect: 'is-card',
        paypal: 'is-paypal',
        rebill: 'is-rebill',
        bank_transfer: 'is-bank',
        cash: 'is-cash',
        fomo_cash: 'is-cash',
        fomo_pos: 'is-card',
        organizer_cash: 'is-cash',
        organizer_pos: 'is-card',
        other: 'is-other',
      }
      return classes[value] || classes.other
    },

    executionModeText(value, fallback = '') {
      const labels = {
        api: 'Procesada automáticamente',
        manual: 'Gestionada por FOMO',
      }
      return labels[value] || fallback || 'Gestionada por FOMO'
    },

    payoutStatusText(value, fallback = '') {
      const labels = {
        draft: 'En preparación',
        approved: 'Aprobada',
        submitted: 'Enviada',
        processing: 'Procesando',
        paid: 'Pagada',
        failed: 'Fallida',
        cancelled: 'Cancelada',
      }
      return labels[value] || fallback || 'Sin estado'
    },

    statusClass(value) {
      if (value === 'paid') return 'is-paid'
      if (value === 'failed' || value === 'cancelled') return 'is-stopped'
      if (value === 'submitted' || value === 'processing') return 'is-moving'
      return 'is-pending'
    },

    settlementStatusText(value, fallback = '') {
      const labels = {
        held: 'En espera',
        blocked: 'Bloqueado',
        releasable: 'Liberado',
        partially_paid: 'Pagado parcialmente',
        paid: 'Pagado',
      }
      return labels[value] || fallback || 'Fondo registrado'
    },

    sourceWindowText(value, fallback = '') {
      const labels = {
        pre_event: 'Antes del evento',
        post_event: 'Después del evento',
      }
      return labels[value] || fallback || 'Periodo del evento'
    },

    payoutReference(order) {
      if (!order) return 'Referencia pendiente'
      return order.external_reference || order.provider_reference || 'Referencia pendiente'
    },

    includedSourcesLabel(order) {
      const count = Number(order && (order.items_count ?? (Array.isArray(order.items) ? order.items.length : 0))) || 0
      return `${count} ${count === 1 ? 'origen usado' : 'orígenes usados'}`
    },

    payoutItemsTotal(order) {
      if (!order) return 0
      if (order.items_total !== undefined && order.items_total !== null && order.items_total !== '') {
        return Number(order.items_total) || 0
      }
      return (Array.isArray(order.items) ? order.items : []).reduce((total, item) => total + (Number(item.amount) || 0), 0)
    },

    payoutIsReconciled(order) {
      if (!order) return false
      if (typeof order.items_reconciled === 'boolean') return order.items_reconciled
      return Math.abs(this.payoutItemsTotal(order) - (Number(order.amount) || 0)) < 0.005
    },

    itemSourceText(item, order) {
      const bucket = item && item.funding_bucket_detail
      if (bucket) return this.fundingSourceText(bucket.source_provider, bucket.source_provider_label)
      return this.fundingSourceText(order && order.funding_source, order && order.funding_source_label)
    },

    itemSourceCurrency(item, order) {
      const bucket = item && item.funding_bucket_detail
      return (bucket && bucket.currency) || (item && item.settlement_currency) || (order && order.currency) || this.eventCurrency
    },

    itemSourceTotal(item) {
      const bucket = item && item.funding_bucket_detail
      if (!bucket) return Number(item && item.amount) || 0
      return Number(bucket.net_payable_amount || bucket.collected_amount) || 0
    },

    itemUnallocatedBalance(item) {
      const bucket = item && item.funding_bucket_detail
      if (!bucket) return 0
      const total = this.itemSourceTotal(item)
      const paid = Number(bucket.paid_amount) || 0
      const reserved = Number(bucket.reserved_amount) || 0
      return Math.max(total - paid - reserved, 0)
    },

    itemIsPartial(item) {
      return this.itemSourceTotal(item) - (Number(item && item.amount) || 0) > 0.005
    },

    partialSourcesCount(order) {
      return (Array.isArray(order && order.items) ? order.items : []).filter((item) => this.itemIsPartial(item)).length
    },

    bucketPaymentPanel(item) {
      const bucketId = item && item.funding_bucket_detail && item.funding_bucket_detail.id
      return this.bucketPaymentPanels[bucketId] || {
        open: false,
        loading: false,
        error: '',
        data: null,
        page: 1,
      }
    },

    updateBucketPaymentPanel(bucketId, updates) {
      const current = this.bucketPaymentPanels[bucketId] || {}
      this.bucketPaymentPanels = {
        ...this.bucketPaymentPanels,
        [bucketId]: { ...current, ...updates },
      }
    },

    bucketPaymentsButtonLabel(item) {
      const bucket = item && item.funding_bucket_detail
      const count = Number(bucket && bucket.payments_count) || 0
      if (!count) return 'Ver movimientos del bucket'
      return `Ver ${count} ${count === 1 ? 'movimiento' : 'movimientos'} del bucket`
    },

    toggleBucketPayments(item) {
      const bucket = item && item.funding_bucket_detail
      if (!bucket || !bucket.id) return
      const panel = this.bucketPaymentPanel(item)
      if (panel.open) {
        this.updateBucketPaymentPanel(bucket.id, { open: false })
        return
      }
      this.updateBucketPaymentPanel(bucket.id, { open: true })
      if (!panel.data && !panel.loading) this.loadBucketPayments(item, 1)
    },

    loadBucketPayments(item, page = 1) {
      const bucket = item && item.funding_bucket_detail
      if (!bucket || !bucket.id || !this.event || !this.event.id) return
      this.updateBucketPaymentPanel(bucket.id, { open: true, loading: true, error: '', page })
      apiService(`/api/host/events/${this.event.id}/payout-buckets/${bucket.id}/payments/?page=${page}&page_size=10`).then((data) => {
        if (!data || data.err) {
          this.updateBucketPaymentPanel(bucket.id, {
            loading: false,
            error: (data && data.err) || 'No pudimos cargar los movimientos de este bucket.',
          })
          return
        }
        this.updateBucketPaymentPanel(bucket.id, {
          loading: false,
          error: '',
          data,
          page: data.pagination && data.pagination.page ? data.pagination.page : page,
        })
      }).catch(() => {
        this.updateBucketPaymentPanel(bucket.id, {
          loading: false,
          error: 'No pudimos cargar los movimientos de este bucket.',
        })
      })
    },

    proofUrl(order) {
      return (order && (order.proof_url || order.proof_file)) || ''
    },

    isImageProof(order) {
      const url = this.proofUrl(order).split('?')[0].toLowerCase()
      return /\.(png|jpe?g|webp|gif|bmp|avif)$/.test(url)
    },

    openPayoutDetails(order) {
      this.selectedOrder = order
      this.proofPreviewFailed = false
      if (typeof document !== 'undefined') {
        this.bodyOverflowBeforeModal = document.body.style.overflow
        document.body.style.overflow = 'hidden'
      }
    },

    restoreBodyScroll() {
      if (typeof document !== 'undefined') {
        document.body.style.overflow = this.bodyOverflowBeforeModal || ''
      }
    },

    closePayoutDetails() {
      if (!this.selectedOrder) return
      this.selectedOrder = null
      this.proofPreviewFailed = false
      this.bucketPaymentPanels = {}
      this.restoreBodyScroll()
    },

    handleModalKeydown(event) {
      if (event.key === 'Escape' && this.selectedOrder) this.closePayoutDetails()
    },

    getPayoutSummary() {
      if (!this.event || !this.event.id) {
        return
      }

      this.payoutLoading = true
      this.payoutError = ''
      const requestId = ++this.payoutRequestId
      apiService(`/api/host/events/${this.event.id}/payouts/`).then((data) => {
        if (requestId !== this.payoutRequestId) return
        if (data && data.err) {
          this.settlements = []
          this.orders = []
          this.cashierMethods = []
          this.payoutError = data.err
          return
        }

        this.settlements = data && data.settlements ? data.settlements : []
        this.orders = data && data.orders ? data.orders : []
        this.cashierMethods = data && data.cashier_methods ? data.cashier_methods : []
      }).catch(() => {
        if (requestId !== this.payoutRequestId) return
        this.settlements = []
        this.orders = []
        this.cashierMethods = []
        this.payoutError = 'No pudimos cargar la informacion de payouts.'
      }).finally(() => {
        if (requestId === this.payoutRequestId) this.payoutLoading = false
      })
    },
  },

  computed: {
    eventCurrency() {
      return normalizeCurrencyCode(this.event && this.event.presentment_currency, 'USD')
    },

    canViewInternalFinance() {
      return Boolean(this.event && this.event.can_view_internal_finance)
    },

    showInternalFinance() {
      return this.canViewInternalFinance && !this.hostViewPreview
    },

    organizerGrossAmount() {
      return (Number(this.event && this.event.ledger_organizer_sales_gross) || 0) + this.organizerDirectAmount
    },

    organizerDirectAmount() {
      return this.cashierMethods
        .filter(method => method.funds_recipient === 'organizer' && normalizeCurrencyCode(method.currency, this.eventCurrency) === this.eventCurrency)
        .reduce((total, method) => total + (Number(method.organizer_net) || 0), 0)
    },

    organizerDepositedAmount() {
      return Number(this.event && this.event.ledger_organizer_deposited) || 0
    },

    organizerPendingAmount() {
      return Number(this.event && this.event.ledger_organizer_pending) || 0
    },

    organizerPaidProgress() {
      if (this.organizerGrossAmount <= 0) return 0
      const progress = ((this.organizerDepositedAmount + this.organizerDirectAmount) / this.organizerGrossAmount) * 100
      return Math.min(Math.max(progress, 0), 100)
    },

    settlementSummaries() {
      const summaries = new Map()

      this.settlements.forEach((settlement) => {
        const currency = normalizeCurrencyCode(settlement.currency, this.eventCurrency)
        const current = summaries.get(currency) || {
          currency,
          available: 0,
          preEvent: 0,
          postEvent: 0,
          reserved: 0,
          paid: 0,
          held: 0,
          remaining: 0,
          statuses: new Set(),
        }
        current.available += Number(settlement.available_to_order) || 0
        current.preEvent += Number(settlement.releasable_pre_event) || 0
        current.postEvent += Number(settlement.releasable_post_event) || 0
        current.reserved += Number(settlement.reserved_amount) || 0
        current.paid += Number(settlement.paid_amount) || 0
        current.held += Number(settlement.held_amount) || 0
        current.remaining += Number(settlement.remaining_amount) || 0
        if (settlement.status) current.statuses.add(settlement.status)
        summaries.set(currency, current)
      })

      return Array.from(summaries.values()).map((summary) => {
        // Reserved funds are still pending until the transfer is paid.
        const pendingTotal = summary.remaining + summary.reserved > 0
          ? summary.remaining + summary.reserved
          : summary.available + summary.reserved + summary.held
        const availableForBar = Math.min(Math.max(summary.available, 0), pendingTotal)
        const afterAvailable = Math.max(pendingTotal - availableForBar, 0)
        const reservedForBar = Math.min(Math.max(summary.reserved, 0), afterAvailable)
        const afterReserved = Math.max(afterAvailable - reservedForBar, 0)
        const heldForBar = Math.min(Math.max(summary.held, 0), afterReserved)
        const pendingShare = (value) => pendingTotal > 0 ? (value / pendingTotal) * 100 : 0
        return {
          ...summary,
          totalReleased: summary.preEvent + summary.postEvent,
          pendingTotal,
          availableShare: pendingShare(availableForBar),
          reservedShare: pendingShare(reservedForBar),
          heldShare: pendingShare(heldForBar),
          statusLabel: summary.statuses.size === 1
            ? this.settlementStatusText(Array.from(summary.statuses)[0])
            : 'Saldo consolidado',
        }
      }).sort((left, right) => {
        if (left.currency === this.eventCurrency) return -1
        if (right.currency === this.eventCurrency) return 1
        return left.currency.localeCompare(right.currency)
      })
    },

    fundingMethodBreakdown() {
      const methods = new Map()

      this.settlements.forEach((settlement) => {
        const settlementCurrency = normalizeCurrencyCode(settlement.currency, this.eventCurrency)
        const buckets = Array.isArray(settlement.funding_buckets) ? settlement.funding_buckets : []
        buckets.forEach((bucket) => {
          const currency = normalizeCurrencyCode(bucket.currency, settlementCurrency)
          const source = bucket.source_provider || 'other'
          const key = `${currency}:${source}`
          const current = methods.get(key) || {
            key,
            source,
            currency,
            label: this.fundingSourceText(source, bucket.label || bucket.source_provider_label),
            amount: 0,
            paid: 0,
            reserved: 0,
            available: 0,
            paymentsCount: 0,
          }
          current.amount += Number(bucket.net_payable_amount) || 0
          current.paid += Number(bucket.paid_amount) || 0
          current.reserved += Number(bucket.reserved_amount) || 0
          current.available += Number(bucket.available_to_order) || 0
          current.paymentsCount += Number(bucket.payments_count) || 0
          methods.set(key, current)
        })
      })

      this.cashierMethods.forEach((method) => {
        const currency = normalizeCurrencyCode(method.currency, this.eventCurrency)
        const source = method.source_provider
        const key = `${currency}:${source}`
        const direct = method.funds_recipient === 'organizer'
        const amount = Number(method.organizer_net) || 0
        const current = methods.get(key) || {
          key, source, currency, label: this.fundingSourceText(source),
          amount, paid: direct ? amount : 0, reserved: 0, available: 0,
          paymentsCount: Number(method.payments_count) || 0,
        }
        current.direct = direct
        current.charged = Number(method.charged_amount) || 0
        current.refunded = Number(method.refunded_amount) || 0
        methods.set(key, current)
      })

      const currencyTotals = new Map()
      methods.forEach((method) => {
        currencyTotals.set(method.currency, (currencyTotals.get(method.currency) || 0) + method.amount)
      })

      return Array.from(methods.values()).filter((method) => method.amount > 0 || method.paymentsCount > 0 || method.refunded > 0).map((method) => {
        const currencyTotal = currencyTotals.get(method.currency) || 0
        const rawShare = currencyTotal > 0 ? (method.amount / currencyTotal) * 100 : 0
        const amount = Math.max(method.amount, 0)
        const paidForBar = Math.min(Math.max(method.paid, 0), amount)
        const afterPaid = Math.max(amount - paidForBar, 0)
        const reservedForBar = Math.min(Math.max(method.reserved, 0), afterPaid)
        const afterReserved = Math.max(afterPaid - reservedForBar, 0)
        const availableForBar = Math.min(Math.max(method.available, 0), afterReserved)
        const pending = Math.max(afterReserved - availableForBar, 0)
        const allocationShare = (value) => amount > 0 ? (value / amount) * 100 : 0
        return {
          ...method,
          share: method.amount > 0 ? Math.max(Math.min(rawShare, 100), 3) : 0,
          shareLabel: `${Math.round(rawShare)}%`,
          paidShare: allocationShare(paidForBar),
          paidShareLabel: `${Math.round(allocationShare(paidForBar))}%`,
          reservedShare: allocationShare(reservedForBar),
          availableShare: allocationShare(availableForBar),
          pending,
        }
      }).sort((left, right) => {
        if (left.currency === this.eventCurrency && right.currency !== this.eventCurrency) return -1
        if (right.currency === this.eventCurrency && left.currency !== this.eventCurrency) return 1
        if (left.currency !== right.currency) return left.currency.localeCompare(right.currency)
        return right.amount - left.amount
      })
    },

    ledgerApprovedCount() {
      return this.formatCount(this.event.ledger_approved_payments)
    },

    ledgerZeroValueCount() {
      return this.formatCount(this.event.ledger_zero_value_approved_payments)
    },

    ledgerMonetaryApprovedCount() {
      return Math.max(this.ledgerApprovedCount - this.ledgerZeroValueCount, 0)
    },

    ledgerPostedCount() {
      return this.formatCount(this.event.ledger_posted_payments)
    },

    ledgerMissingCount() {
      return this.formatCount(this.event.ledger_missing_posted_payments)
    },

    ledgerHealthCopy() {
      const missing = this.ledgerMissingCount
      const skipped = this.ledgerZeroValueCount
      const posted = this.ledgerPostedCount

      if (missing === 0) {
        return skipped > 0
          ? `${posted} pagos monetarios posteados y ${skipped} aprobados 0.00 omitidos por diseño.`
          : `${posted} pagos monetarios ya reconciliados en ledger.`
      }

      return `${posted} pagos posteados, ${skipped} aprobados 0.00 omitidos y ${missing} pagos monetarios aún sin asiento.`
    },
  },
}
</script>

<style scoped>
.finance-workspace {
  padding: 22px;
  font-family: 'Space Grotesk', sans-serif;
  backdrop-filter: blur(24px) saturate(142%);
  -webkit-backdrop-filter: blur(24px) saturate(142%);
}

.finance-workspace::before {
  content: none;
  display: none;
}

.finance-hero .workspace-title {
  max-width: 920px;
  margin-top: 14px;
  font-family: 'Syne', sans-serif;
  font-size: clamp(2.25rem, 4vw, 3.35rem);
  letter-spacing: -0.045em;
  line-height: 0.98;
}

.finance-hero .workspace-copy {
  max-width: 760px;
  margin-top: 16px;
  color: rgba(221, 223, 233, 0.68);
  font-size: clamp(0.98rem, 1.5vw, 1.12rem);
}

.finance-header-actions {
  display: grid;
  justify-items: end;
  gap: 12px;
}

.finance-health {
  display: grid;
  gap: 4px;
  max-width: 320px;
  padding: 13px 15px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.025);
}

.finance-health--ok {
  border-color: rgba(140, 243, 232, 0.28);
  box-shadow: inset 0 0 0 1px rgba(140, 243, 232, 0.08);
}

.finance-health--warn {
  border-color: rgba(255, 193, 122, 0.28);
  box-shadow: inset 0 0 0 1px rgba(196, 206, 228, 0.08);
}

.finance-health small {
  color: rgba(208, 219, 238, 0.72);
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.finance-health strong {
  color: var(--text-main);
  font-size: 1.45rem;
  line-height: 1;
}

.finance-health span {
  color: var(--text-soft);
  line-height: 1.45;
}

.finance-overview {
  display: grid;
  grid-template-columns: minmax(0, 1.32fr) minmax(340px, 0.68fr);
  gap: 16px;
  margin-bottom: 20px;
}

.finance-balance-card,
.finance-availability {
  min-height: 306px;
}

.finance-balance-card {
  padding: 26px;
}

.finance-availability {
  padding: 24px;
}

.finance-card-heading,
.finance-section-heading,
.finance-method__body > header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
}

.finance-card-heading h3,
.finance-section-heading h3 {
  margin: 6px 0 0;
  color: var(--text-main);
  font-family: 'Syne', sans-serif;
  font-size: 1.08rem;
  letter-spacing: -0.015em;
}

.finance-eyebrow {
  color: rgba(219, 221, 231, 0.58);
  font-size: 0.67rem;
  font-weight: 850;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.finance-currency-chip {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 0 11px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  color: rgba(240, 242, 248, 0.78);
  background: rgba(7, 9, 14, 0.28);
  font-size: 0.7rem;
  font-weight: 850;
  letter-spacing: 0.12em;
}

.finance-balance-card__amount {
  display: block;
  margin-top: 25px;
  color: #f5f6fb;
  font-family: 'Syne', sans-serif;
  font-size: clamp(2.65rem, 5vw, 4.4rem);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.06em;
  line-height: 0.94;
}

.finance-balance-card > p {
  margin: 10px 0 0;
  color: rgba(218, 220, 230, 0.58);
  font-size: 0.88rem;
}

.finance-progress {
  height: 6px;
  margin-top: 27px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.075);
}

.finance-progress span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #d8ceff, #9d82e8);
  box-shadow: 0 0 20px rgba(157, 130, 232, 0.26);
  transition: width 360ms ease;
}

.finance-balance-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 18px;
}

.finance-balance-stats article {
  display: grid;
  gap: 4px;
  padding: 13px 14px;
  border: 1px solid rgba(196, 206, 228, 0.09);
  border-radius: 15px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.018), rgba(255, 255, 255, 0.006)),
    rgba(7, 8, 13, 0.78);
}

.finance-balance-stats small,
.finance-admin-strip small {
  color: rgba(220, 222, 232, 0.5);
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.finance-balance-stats strong {
  color: rgba(246, 247, 251, 0.92);
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.16rem;
  font-variant-numeric: tabular-nums;
}

.finance-balance-stats span {
  color: rgba(213, 216, 227, 0.48);
  font-size: 0.72rem;
}

.finance-availability .finance-card-heading > i {
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 12px;
  color: rgba(207, 211, 223, 0.78);
  background: rgba(255, 255, 255, 0.035);
}

.finance-availability__list {
  display: grid;
  gap: 18px;
  margin-top: 28px;
}

.finance-availability__currency + .finance-availability__currency {
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
}

.finance-pending-intro {
  display: flex;
  align-items: center;
  gap: 10px;
}

.finance-pending-intro > span {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 10px;
  border: 1px solid rgba(196, 206, 228, 0.12);
  border-radius: 999px;
  color: #d8d0f2;
  background: rgba(255, 255, 255, 0.035);
  font-size: 0.66rem;
  font-weight: 850;
  letter-spacing: 0.1em;
}

.finance-pending-intro p {
  margin: 0;
  color: rgba(216, 219, 229, 0.54);
  font-size: 0.74rem;
  line-height: 1.4;
}

.finance-pending-track {
  display: flex;
  height: 8px;
  margin-top: 22px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.065);
}

.finance-pending-track span {
  display: block;
  height: 100%;
}

.finance-pending-track .is-ready,
.finance-pending-details i.is-ready {
  background: #d6dff0;
}

.finance-pending-track .is-moving,
.finance-pending-details i.is-moving {
  background: #9d82e8;
}

.finance-pending-track .is-held,
.finance-pending-details i.is-held {
  background: #4f465f;
}

.finance-pending-details {
  display: grid;
  gap: 10px;
  margin: 16px 0 0;
}

.finance-pending-details > div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.finance-pending-details dt,
.finance-pending-details dd {
  margin: 0;
  font-size: 0.75rem;
}

.finance-pending-details dt {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: rgba(216, 219, 229, 0.5);
}

.finance-pending-details dt i {
  width: 7px;
  height: 7px;
  flex: 0 0 auto;
  border-radius: 999px;
}

.finance-pending-details dd {
  color: rgba(243, 244, 248, 0.82);
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 750;
  font-variant-numeric: tabular-nums;
}

.finance-inline-state {
  display: flex;
  align-items: center;
  min-height: 190px;
  justify-content: center;
  gap: 9px;
  color: rgba(216, 219, 229, 0.55);
  text-align: center;
}

.finance-inline-state.is-error {
  color: #ffd1b0;
}

.finance-methods {
  display: grid;
  gap: 20px;
  margin-bottom: 20px;
  padding: 23px;
}

.finance-section-heading {
  align-items: end;
}

.finance-section-heading > p {
  max-width: 390px;
  margin: 0;
  color: rgba(216, 219, 229, 0.52);
  font-size: 0.8rem;
  text-align: right;
}

.finance-methods__list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.finance-method {
  --method-accent: #aeb5c1;
  display: flex;
  gap: 14px;
  min-width: 0;
  padding: 16px;
}

.finance-method.is-card {
  --method-accent: #a99fe0;
}

.finance-method.is-paypal {
  --method-accent: #7eadd6;
}

.finance-method.is-rebill {
  --method-accent: #8bcbbb;
}

.finance-method.is-bank {
  --method-accent: #aab5c4;
}

.finance-method.is-cash {
  --method-accent: #a99fe0;
}

.finance-method__collection {
  margin: 12px 0 0;
  color: var(--text-soft);
  font-size: 0.75rem;
  line-height: 1.6;
}

.finance-method__icon {
  display: grid;
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  place-items: center;
  border: 1px solid color-mix(in srgb, var(--method-accent) 28%, transparent);
  border-radius: 12px;
  color: var(--method-accent);
  background: rgba(255, 255, 255, 0.035);
}

.finance-method__body {
  min-width: 0;
  flex: 1;
}

.finance-method__body > header > div {
  display: grid;
  min-width: 0;
  gap: 3px;
}

.finance-method__body > header strong {
  color: rgba(245, 246, 250, 0.9);
  font-size: 0.9rem;
}

.finance-method__body > header > strong {
  flex: 0 0 auto;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.96rem;
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.finance-method__body > header span {
  color: rgba(215, 218, 228, 0.46);
  font-size: 0.68rem;
}

.finance-method__bar-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 13px;
  color: rgba(215, 218, 228, 0.48);
  font-size: 0.64rem;
}

.finance-method__bar-label strong {
  color: rgba(234, 236, 244, 0.7);
  font-family: 'Space Grotesk', sans-serif;
  font-size: inherit;
  font-variant-numeric: tabular-nums;
}

.finance-method__allocation {
  display: flex;
  height: 6px;
  margin-top: 6px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.065);
}

.finance-method__allocation span {
  display: block;
  height: 100%;
  transition: width 360ms ease;
}

.finance-method__allocation .is-paid {
  background: #9d82e8;
}

.finance-method__allocation .is-reserved {
  background: #75659d;
}

.finance-method__allocation .is-available {
  background: #d6dff0;
}

.finance-method__body footer {
  display: flex;
  flex-wrap: wrap;
  gap: 5px 13px;
  margin-top: 10px;
  color: rgba(213, 216, 226, 0.48);
  font-size: 0.66rem;
}

.finance-method__legend {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-variant-numeric: tabular-nums;
}

.finance-method__legend i {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.2);
}

.finance-method__legend.is-paid i {
  background: #9d82e8;
}

.finance-method__legend.is-reserved i {
  background: #75659d;
}

.finance-method__legend.is-available i {
  background: #d6dff0;
}

.finance-method__legend.is-pending i {
  background: rgba(255, 255, 255, 0.18);
}

.finance-admin-strip {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-bottom: 20px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 18px;
  background: rgba(4, 6, 10, 0.18);
}

.finance-admin-strip article {
  display: grid;
  gap: 6px;
  padding: 15px 17px;
}

.finance-admin-strip article + article {
  border-left: 1px solid rgba(255, 255, 255, 0.065);
}

.finance-admin-strip strong {
  color: rgba(245, 246, 250, 0.88);
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1rem;
  font-variant-numeric: tabular-nums;
}

.ledger-reconciliation {
  display: grid;
  gap: 20px;
  padding: 22px;
  overflow: hidden;
  border: 1px solid rgba(180, 163, 255, 0.18);
  border-radius: 24px;
  background:
    radial-gradient(circle at 90% 0%, rgba(130, 105, 255, 0.13), transparent 32%),
    linear-gradient(145deg, rgba(255, 255, 255, 0.045), rgba(255, 255, 255, 0.018));
}

.ledger-reconciliation__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
}

.ledger-reconciliation__eyebrow {
  color: #b8aaff;
  font-size: 0.68rem;
  font-weight: 850;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.ledger-reconciliation__header h3 {
  margin: 6px 0 4px;
  color: var(--text-main);
  font-family: 'Syne', sans-serif;
  font-size: 1.12rem;
}

.ledger-reconciliation__header p {
  max-width: 680px;
  margin: 0;
  color: var(--text-soft);
  line-height: 1.5;
}

.ledger-reconciliation__status {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  border: 1px solid rgba(223, 214, 255, 0.12);
  border-radius: 999px;
  color: var(--text-soft);
  background: rgba(12, 15, 25, 0.45);
  font-size: 0.76rem;
  font-weight: 800;
}

.ledger-reconciliation__status.is-ok {
  border-color: rgba(140, 243, 232, 0.28);
  color: #a8f7e8;
  background: rgba(68, 186, 161, 0.09);
}

.ledger-reconciliation__status.is-warning {
  border-color: rgba(255, 193, 122, 0.32);
  color: #ffd49e;
  background: rgba(255, 165, 82, 0.08);
}

.ledger-equation {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr) auto minmax(0, 1.12fr);
  align-items: stretch;
  gap: 12px;
}

.ledger-equation__card {
  display: grid;
  align-content: center;
  min-height: 116px;
  padding: 16px;
  border: 1px solid rgba(223, 214, 255, 0.1);
  border-radius: 18px;
  background: rgba(7, 10, 18, 0.34);
}

.ledger-equation__card small,
.ledger-flow small {
  color: rgba(208, 219, 238, 0.72);
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.ledger-equation__card strong {
  margin: 7px 0;
  color: var(--text-main);
  font-size: 2rem;
  line-height: 1;
}

.ledger-equation__card span {
  color: var(--text-soft);
  font-size: 0.8rem;
  line-height: 1.35;
}

.ledger-equation__card--result {
  border-color: rgba(184, 170, 255, 0.3);
  background: linear-gradient(145deg, rgba(121, 94, 255, 0.16), rgba(121, 94, 255, 0.06));
  box-shadow: inset 0 0 0 1px rgba(184, 170, 255, 0.04);
}

.ledger-equation__card--result strong {
  color: #d8d0ff;
}

.ledger-equation__operator {
  align-self: center;
  color: rgba(216, 208, 255, 0.58);
  font-size: 1.35rem;
  font-weight: 800;
}

.ledger-flow {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  padding: 14px;
  border: 1px solid rgba(223, 214, 255, 0.08);
  border-radius: 18px;
  background: rgba(7, 10, 18, 0.25);
}

.ledger-flow article {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
  padding: 10px 12px;
  border-radius: 12px;
}

.ledger-flow strong {
  color: var(--text-main);
  font-size: 1.35rem;
}

.ledger-flow__arrow {
  color: rgba(216, 208, 255, 0.38);
  font-size: 0.78rem;
}

.ledger-flow__result.is-ok {
  background: rgba(68, 186, 161, 0.1);
}

.ledger-flow__result.is-ok strong {
  color: #a8f7e8;
}

.ledger-flow__result.is-warning {
  background: rgba(255, 165, 82, 0.1);
}

.ledger-flow__result.is-warning strong {
  color: #ffd49e;
}

.ledger-reconciliation__source {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: rgba(208, 219, 238, 0.58);
  font-size: 0.76rem;
  line-height: 1.45;
}

.ledger-reconciliation__source i {
  margin-top: 2px;
  color: rgba(184, 170, 255, 0.72);
}

.workspace-table-shell {
  overflow: auto;
}

.workspace-table {
  width: 100%;
  border-collapse: collapse;
}

.workspace-table th,
.workspace-table td {
  padding: 14px 12px;
  border-bottom: 1px solid rgba(223, 214, 255, 0.08);
  text-align: left;
  color: var(--text-soft);
}

.workspace-table th {
  color: var(--text-main);
  font-size: 0.75rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.payout-table tbody tr {
  transition: background 180ms ease, box-shadow 180ms ease;
}

.payout-table tbody tr:hover,
.payout-table tbody tr.is-open {
  background: rgba(174, 151, 255, 0.045);
  box-shadow: inset 3px 0 0 rgba(174, 151, 255, 0.48);
}

.payout-table__primary {
  display: grid;
  gap: 4px;
  min-width: 132px;
}

.payout-table__primary strong,
.payout-table__amount {
  color: var(--text-main);
  font-weight: 800;
}

.payout-table__primary small {
  color: rgba(208, 219, 238, 0.62);
  font-size: 0.74rem;
}

.payout-table__amount {
  font-family: 'Space Grotesk', sans-serif;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.payout-table__action {
  text-align: right !important;
}

.payout-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 30px;
  padding: 6px 10px;
  border: 1px solid rgba(223, 214, 255, 0.13);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
  color: rgba(226, 232, 244, 0.82);
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.payout-status.is-paid {
  border-color: rgba(184, 165, 255, 0.34);
  background: rgba(154, 125, 255, 0.12);
  color: #ddd4ff;
}

.payout-status.is-moving {
  border-color: rgba(146, 174, 255, 0.3);
  background: rgba(126, 150, 255, 0.1);
  color: #d7e0ff;
}

.payout-status.is-stopped {
  border-color: rgba(255, 137, 159, 0.3);
  background: rgba(255, 111, 138, 0.1);
  color: #ffb9c7;
}

.payout-detail-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 36px;
  padding: 8px 12px;
  border: 1px solid rgba(174, 151, 255, 0.24);
  border-radius: 12px;
  background: rgba(154, 125, 255, 0.08);
  color: #ded6ff;
  font-size: 0.76rem;
  font-weight: 900;
  cursor: pointer;
  white-space: nowrap;
  transition: transform 180ms ease, border-color 180ms ease, background 180ms ease;
}

.payout-detail-button:hover,
.payout-detail-button:focus-visible {
  border-color: rgba(184, 165, 255, 0.56);
  background: rgba(154, 125, 255, 0.16);
  transform: translateY(-1px);
  outline: none;
}

.payout-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2400;
  display: grid;
  place-items: center;
  padding: 24px;
  background: rgba(2, 3, 8, 0.8);
  backdrop-filter: blur(18px);
  font-family: 'Space Grotesk', sans-serif;
}

.payout-modal {
  width: min(1120px, 100%);
  max-height: calc(100vh - 48px);
  overflow: hidden;
  border: 1px solid rgba(202, 214, 228, 0.14);
  border-radius: 30px;
  background:
    radial-gradient(circle at 92% 0%, rgba(90, 105, 126, 0.16), transparent 32%),
    linear-gradient(180deg, rgba(18, 19, 28, 0.99), rgba(7, 8, 13, 0.995));
  box-shadow: 0 32px 90px rgba(0, 0, 0, 0.62), inset 0 1px 0 rgba(255, 255, 255, 0.06);
}

.payout-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  padding: 24px 26px 20px;
  border-bottom: 1px solid rgba(202, 214, 228, 0.1);
  background: rgba(10, 11, 17, 0.78);
}

.payout-modal__header h3,
.payout-detail-card h4 {
  margin: 4px 0 0;
  color: #f6fbff;
  font-family: 'Syne', sans-serif;
}

.payout-modal__header h3 {
  font-size: clamp(1.55rem, 3vw, 2.2rem);
  letter-spacing: -0.04em;
}

.payout-modal__header p,
.payout-detail-card__heading p {
  margin: 7px 0 0;
  color: rgba(202, 214, 223, 0.7);
  line-height: 1.4;
}

.payout-modal__eyebrow {
  color: #bcaaff;
  font-size: 0.66rem;
  font-weight: 900;
  letter-spacing: 0.17em;
  text-transform: uppercase;
}

.payout-modal__header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.payout-modal__close {
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border: 1px solid rgba(202, 214, 228, 0.14);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);
  color: #eef5ff;
  cursor: pointer;
}

.payout-modal__close:hover,
.payout-modal__close:focus-visible {
  border-color: rgba(184, 165, 255, 0.5);
  color: #ded6ff;
  outline: none;
}

.payout-modal__body {
  display: grid;
  gap: 16px;
  max-height: calc(100vh - 155px);
  padding: 20px 24px 26px;
  overflow: auto;
}

.payout-detail-summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.payout-detail-summary article,
.payout-detail-card {
  border: 1px solid rgba(202, 214, 228, 0.1);
  background: rgba(255, 255, 255, 0.025);
}

.payout-detail-summary article {
  display: grid;
  gap: 5px;
  padding: 14px;
  border-radius: 17px;
}

.payout-detail-summary small,
.payout-definition-list dt,
.payout-note small,
.payout-reconciliation small,
.payout-included-item__title small,
.payout-bucket-details small {
  color: rgba(201, 214, 230, 0.62);
  font-size: 0.67rem;
  font-weight: 900;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.payout-detail-summary strong {
  color: #f5f9ff;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.94rem;
  font-variant-numeric: tabular-nums;
}

.payout-modal__columns {
  display: grid;
  grid-template-columns: minmax(0, 1.12fr) minmax(300px, 0.88fr);
  gap: 16px;
}

.payout-detail-card {
  padding: 18px;
  border-radius: 22px;
}

.payout-detail-card__heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 15px;
}

.payout-detail-card h4 {
  font-size: 1.05rem;
}

.payout-text-link,
.payout-proof-file a {
  color: #cfc2ff;
  font-size: 0.75rem;
  font-weight: 900;
  text-decoration: none;
}

.payout-text-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.payout-proof-preview {
  position: relative;
  display: grid;
  min-height: 280px;
  overflow: hidden;
  border: 1px solid rgba(202, 214, 228, 0.13);
  border-radius: 18px;
  background:
    linear-gradient(45deg, rgba(255, 255, 255, 0.018) 25%, transparent 25%, transparent 75%, rgba(255, 255, 255, 0.018) 75%),
    #090b11;
  background-size: 22px 22px;
}

.payout-proof-preview img {
  width: 100%;
  height: 100%;
  max-height: 430px;
  object-fit: contain;
}

.payout-proof-preview span {
  position: absolute;
  right: 12px;
  bottom: 12px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 11px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 999px;
  background: rgba(5, 7, 11, 0.84);
  color: #f2f8ff;
  font-size: 0.72rem;
  font-weight: 900;
  backdrop-filter: blur(10px);
}

.payout-proof-file,
.payout-proof-empty {
  display: grid;
  place-items: center;
  align-content: center;
  gap: 8px;
  min-height: 280px;
  padding: 24px;
  border: 1px dashed rgba(202, 214, 228, 0.16);
  border-radius: 18px;
  background: rgba(6, 8, 13, 0.5);
  color: rgba(202, 214, 223, 0.72);
  text-align: center;
}

.payout-proof-file > i,
.payout-proof-empty > i {
  color: #bcaaff;
  font-size: 1.8rem;
}

.payout-proof-file strong,
.payout-proof-empty strong {
  color: #edf6ff;
}

.payout-proof-file span,
.payout-proof-empty span {
  max-width: 390px;
  line-height: 1.45;
}

.payout-proof-empty--compact {
  min-height: 150px;
}

.payout-definition-list {
  display: grid;
  gap: 0;
  margin: 0;
}

.payout-definition-list div {
  display: grid;
  grid-template-columns: minmax(125px, 0.8fr) minmax(0, 1.2fr);
  gap: 12px;
  padding: 11px 0;
  border-bottom: 1px solid rgba(202, 214, 228, 0.08);
}

.payout-definition-list dd {
  margin: 0;
  color: #edf4ff;
  font-weight: 750;
  text-align: right;
}

.payout-note {
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(154, 125, 255, 0.07);
}

.payout-note.is-danger {
  background: rgba(255, 111, 138, 0.08);
}

.payout-note p {
  margin: 5px 0 0;
  color: rgba(225, 233, 245, 0.8);
  line-height: 1.45;
}

.payout-count-badge {
  display: inline-flex;
  padding: 7px 10px;
  border: 1px solid rgba(202, 214, 228, 0.14);
  border-radius: 999px;
  background: rgba(154, 125, 255, 0.1);
  color: #ded6ff;
  font-size: 0.7rem;
  font-weight: 900;
  white-space: nowrap;
}

.payout-allocation-note {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  margin: -2px 0 14px;
  padding: 12px 14px;
  border: 1px solid rgba(202, 214, 228, 0.12);
  border-radius: 15px;
  background: rgba(134, 150, 171, 0.055);
}

.payout-allocation-note > i {
  margin-top: 3px;
  color: #bcaaff;
}

.payout-allocation-note > div {
  display: grid;
  gap: 3px;
}

.payout-allocation-note strong {
  color: #e5deff;
  font-size: 0.82rem;
}

.payout-allocation-note span {
  color: rgba(220, 212, 248, 0.72);
  font-size: 0.76rem;
  line-height: 1.45;
}

.payout-partial-summary {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  margin: -2px 0 14px;
  padding: 12px 14px;
  border: 1px solid rgba(255, 193, 122, 0.24);
  border-radius: 15px;
  background: rgba(255, 193, 122, 0.07);
  color: #ffd5a4;
}

.payout-partial-summary > i {
  margin-top: 3px;
}

.payout-partial-summary > div {
  display: grid;
  gap: 3px;
}

.payout-partial-summary strong {
  color: #ffe5c7;
  font-size: 0.82rem;
}

.payout-partial-summary span {
  color: rgba(255, 229, 199, 0.76);
  font-size: 0.76rem;
  line-height: 1.45;
}

.payout-included-list {
  display: grid;
  gap: 10px;
}

.payout-included-item {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr);
  gap: 12px;
  padding: 14px;
  border: 1px solid rgba(202, 214, 228, 0.1);
  border-radius: 17px;
  background: rgba(6, 8, 13, 0.46);
}

.payout-included-item__index {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: 11px;
  background: rgba(154, 125, 255, 0.14);
  color: #ded6ff;
  font-weight: 900;
}

.payout-included-item__copy {
  min-width: 0;
}

.payout-included-item__title {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.payout-included-item__title > div {
  display: grid;
  gap: 4px;
}

.payout-included-item__title strong {
  color: #f2f7ff;
}

.payout-included-item__amount {
  justify-items: end;
  text-align: right;
  white-space: nowrap;
}

.payout-partial-pill {
  justify-self: start;
  margin-top: 3px;
  padding: 4px 7px;
  border: 1px solid rgba(255, 193, 122, 0.22);
  border-radius: 999px;
  background: rgba(255, 193, 122, 0.08);
  color: #ffd5a4;
  font-size: 0.62rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.payout-included-item__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  margin-top: 10px;
  color: rgba(202, 214, 223, 0.68);
  font-size: 0.75rem;
}

.payout-included-item__meta span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.payout-included-item__meta i {
  color: rgba(190, 171, 255, 0.8);
}

.payout-partial-notice {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin-top: 11px;
  padding: 10px 11px;
  border-radius: 12px;
  background: rgba(255, 193, 122, 0.065);
  color: rgba(255, 229, 199, 0.78);
  font-size: 0.75rem;
  line-height: 1.5;
}

.payout-partial-notice > i {
  margin-top: 3px;
  color: #ffc17a;
}

.payout-partial-notice strong {
  color: #ffe5c7;
}

.payout-bucket-details {
  margin-top: 12px;
  border-top: 1px solid rgba(202, 214, 228, 0.08);
  padding-top: 10px;
}

.payout-bucket-details summary {
  color: #cfc2ff;
  font-size: 0.72rem;
  font-weight: 900;
  cursor: pointer;
}

.payout-bucket-details > div {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-top: 10px;
}

.payout-bucket-details span {
  display: grid;
  gap: 4px;
  padding: 9px;
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.025);
}

.payout-bucket-details span.is-current {
  border: 1px solid rgba(202, 214, 228, 0.14);
  background: rgba(154, 125, 255, 0.09);
}

.payout-bucket-details strong {
  color: #eaf3ff;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.78rem;
  font-variant-numeric: tabular-nums;
}

.payout-bucket-payments {
  margin-top: 12px;
  padding-top: 11px;
  border-top: 1px solid rgba(202, 214, 228, 0.08);
}

.payout-bucket-payments__toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-height: 40px;
  padding: 9px 12px;
  border: 1px solid rgba(202, 214, 228, 0.14);
  border-radius: 13px;
  background: rgba(255, 255, 255, 0.028);
  color: #d9d1ff;
  font-size: 0.75rem;
  font-weight: 900;
  cursor: pointer;
}

.payout-bucket-payments__toggle > span {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.payout-bucket-payments__toggle:hover,
.payout-bucket-payments__toggle:focus-visible {
  border-color: rgba(184, 165, 255, 0.34);
  background: rgba(154, 125, 255, 0.07);
  outline: none;
}

.payout-bucket-payments__panel {
  display: grid;
  gap: 12px;
  margin-top: 10px;
  padding: 13px;
  border: 1px solid rgba(202, 214, 228, 0.1);
  border-radius: 15px;
  background: rgba(4, 6, 10, 0.36);
}

.payout-bucket-payments__context {
  display: grid;
  grid-template-columns: minmax(165px, 0.42fr) minmax(0, 1fr);
  align-items: center;
  gap: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(202, 214, 228, 0.08);
}

.payout-bucket-payments__context > div {
  display: grid;
  gap: 4px;
}

.payout-bucket-payments__context small {
  color: rgba(201, 214, 230, 0.62);
  font-size: 0.64rem;
  font-weight: 900;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.payout-bucket-payments__context > div strong {
  color: #f4f8ff;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1rem;
  font-variant-numeric: tabular-nums;
}

.payout-bucket-payments__context p {
  margin: 0;
  color: rgba(210, 220, 232, 0.72);
  font-size: 0.75rem;
  line-height: 1.5;
}

.payout-bucket-payments__context p strong {
  color: #efeaff;
}

.payout-bucket-payments__summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.payout-bucket-payments__summary span {
  padding: 7px 9px;
  border: 1px solid rgba(202, 214, 228, 0.1);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.025);
  color: rgba(211, 222, 234, 0.72);
  font-size: 0.68rem;
}

.payout-bucket-payments__summary strong {
  color: #eee9ff;
  font-family: 'Space Grotesk', sans-serif;
  font-variant-numeric: tabular-nums;
}

.payout-bucket-payments__feedback {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 72px;
  color: rgba(211, 222, 234, 0.7);
  font-size: 0.75rem;
  text-align: center;
}

.payout-bucket-payments__feedback.is-error {
  color: #ffbfcc;
}

.payout-bucket-payments__feedback button {
  border: 0;
  background: transparent;
  color: #d9d1ff;
  font-weight: 900;
  cursor: pointer;
  text-decoration: underline;
}

.payout-bucket-payments__table-shell {
  overflow-x: auto;
  border: 1px solid rgba(202, 214, 228, 0.09);
  border-radius: 13px;
}

.payout-bucket-payments__table {
  width: 100%;
  min-width: 690px;
  border-collapse: collapse;
}

.payout-bucket-payments__table th,
.payout-bucket-payments__table td {
  padding: 10px 11px;
  border-bottom: 1px solid rgba(202, 214, 228, 0.075);
  color: rgba(222, 230, 241, 0.78);
  font-size: 0.72rem;
  font-variant-numeric: tabular-nums;
  text-align: left;
  white-space: nowrap;
}

.payout-bucket-payments__table th {
  background: rgba(255, 255, 255, 0.025);
  color: rgba(201, 214, 230, 0.62);
  font-size: 0.61rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.payout-bucket-payments__table tbody tr:last-child td {
  border-bottom: 0;
}

.payout-bucket-payments__table td:first-child {
  display: grid;
  gap: 3px;
}

.payout-bucket-payments__table td:first-child strong,
.payout-bucket-payments__table td.is-contribution {
  color: #f2edff;
  font-weight: 900;
}

.payout-bucket-payments__table td:first-child small {
  color: rgba(201, 214, 230, 0.55);
}

.payout-bucket-payments__pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.payout-bucket-payments__pagination span {
  color: rgba(211, 222, 234, 0.64);
  font-size: 0.7rem;
}

.payout-bucket-payments__pagination button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 9px;
  border: 1px solid rgba(202, 214, 228, 0.12);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.025);
  color: #d9d1ff;
  font-size: 0.69rem;
  font-weight: 900;
  cursor: pointer;
}

.payout-bucket-payments__pagination button:disabled {
  cursor: not-allowed;
  opacity: 0.38;
}

.payout-reconciliation {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr)) auto;
  align-items: center;
  gap: 14px;
  margin-top: 14px;
  padding: 14px 16px;
  border: 1px solid rgba(202, 214, 228, 0.11);
  border-radius: 17px;
  background: rgba(255, 255, 255, 0.025);
}

.payout-reconciliation > div {
  display: grid;
  gap: 4px;
}

.payout-reconciliation strong {
  color: #f1f7ff;
}

.payout-reconciliation > span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 8px 11px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 900;
  white-space: nowrap;
}

.payout-reconciliation.is-ok {
  border-color: rgba(202, 214, 228, 0.16);
}

.payout-reconciliation.is-ok > span {
  background: rgba(154, 125, 255, 0.13);
  color: #ded6ff;
}

.payout-reconciliation.is-warning {
  border-color: rgba(255, 193, 122, 0.28);
}

.payout-reconciliation.is-warning > span {
  background: rgba(255, 193, 122, 0.1);
  color: #ffd5a4;
}

.payout-modal-enter-active,
.payout-modal-leave-active {
  transition: opacity 180ms ease;
}

.payout-modal-enter-active .payout-modal,
.payout-modal-leave-active .payout-modal {
  transition: transform 200ms ease, opacity 180ms ease;
}

.payout-modal-enter-from,
.payout-modal-leave-to {
  opacity: 0;
}

.payout-modal-enter-from .payout-modal,
.payout-modal-leave-to .payout-modal {
  opacity: 0;
  transform: translateY(14px) scale(0.985);
}

@media (max-width: 960px) {
  .finance-overview {
    grid-template-columns: 1fr;
  }

  .finance-balance-card,
  .finance-availability {
    min-height: 0;
  }

  .finance-methods__list {
    grid-template-columns: 1fr;
  }

  .payout-detail-summary,
  .payout-bucket-details > div {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .payout-modal__columns {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .finance-overview {
    gap: 14px;
    margin-bottom: 14px;
  }

  .finance-hero {
    gap: 18px;
  }

  .finance-hero .workspace-title {
    font-size: clamp(2.05rem, 11vw, 2.75rem);
  }

  .finance-header-actions {
    width: 100%;
    justify-items: stretch;
  }

  .finance-balance-card,
  .finance-availability,
  .finance-methods {
    padding: 18px 16px;
    border-radius: 22px;
  }

  .finance-methods {
    margin-bottom: 14px;
  }

  .finance-methods__list {
    gap: 12px;
  }

  .finance-balance-card__amount {
    font-size: clamp(2.4rem, 13vw, 3.25rem);
  }

  .finance-balance-stats {
    grid-template-columns: 1fr;
  }

  .finance-section-heading {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }

  .finance-section-heading > p {
    text-align: left;
  }

  .finance-method {
    padding: 14px;
  }

  .finance-method__body > header {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }

  .finance-method__body > header > strong {
    text-align: left;
  }

  .finance-admin-strip {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .finance-admin-strip article + article {
    border-left: 0;
  }

  .finance-admin-strip article:nth-child(even) {
    border-left: 1px solid rgba(255, 255, 255, 0.065);
  }

  .finance-admin-strip article:nth-child(n + 3) {
    border-top: 1px solid rgba(255, 255, 255, 0.065);
  }

  .ledger-reconciliation {
    gap: 16px;
    padding: 18px 14px;
  }

  .ledger-reconciliation__header {
    align-items: flex-start;
    flex-direction: column;
    gap: 14px;
  }

  .ledger-equation,
  .ledger-flow {
    grid-template-columns: 1fr;
  }

  .ledger-equation__card {
    min-height: 0;
  }

  .ledger-equation__operator {
    justify-self: center;
    line-height: 0.7;
  }

  .ledger-flow__arrow {
    justify-self: center;
    transform: rotate(90deg);
  }

  .payout-modal-backdrop {
    align-items: end;
    padding: 0;
  }

  .payout-modal {
    width: 100%;
    max-height: 94vh;
    border-right: 0;
    border-bottom: 0;
    border-left: 0;
    border-radius: 24px 24px 0 0;
  }

  .payout-modal__header {
    gap: 12px;
    padding: 19px 18px 16px;
  }

  .payout-modal__header-actions {
    align-items: flex-end;
    flex-direction: column-reverse;
  }

  .payout-modal__body {
    max-height: calc(94vh - 116px);
    padding: 16px 14px 24px;
  }

  .payout-detail-summary,
  .payout-bucket-details > div,
  .payout-reconciliation {
    grid-template-columns: 1fr;
  }

  .payout-proof-preview,
  .payout-proof-file,
  .payout-proof-empty {
    min-height: 220px;
  }

  .payout-detail-card__heading,
  .payout-included-item__title {
    align-items: flex-start;
    flex-direction: column;
  }

  .payout-included-item__amount {
    justify-items: start;
    text-align: left;
  }

  .payout-definition-list div {
    grid-template-columns: 1fr;
    gap: 5px;
  }

  .payout-definition-list dd {
    text-align: left;
  }

  .payout-bucket-payments__context {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .payout-bucket-payments__pagination {
    flex-wrap: wrap;
  }
}
</style>
